export interface PixelPoint {
  id: string;
  x: number;
  y: number;
}

interface Group {
  ids: string[];
  pts: { x: number; y: number }[];
  cx: number;
  cy: number;
}

function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

function makeGroup(pts: { x: number; y: number }[], ids: string[]): Group {
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p.x;
    sy += p.y;
  }
  return { ids, pts, cx: sx / pts.length, cy: sy / pts.length };
}

// True when every member of the merged (a + b) group lies within rCap of the merged centroid.
function mergeFits(a: Group, b: Group, rCap: number): boolean {
  const n = a.pts.length + b.pts.length;
  const cx = (a.cx * a.pts.length + b.cx * b.pts.length) / n;
  const cy = (a.cy * a.pts.length + b.cy * b.pts.length) / n;
  const r2 = rCap * rCap;
  for (const p of a.pts) if (distSq(p.x, p.y, cx, cy) > r2) return false;
  for (const p of b.pts) if (distSq(p.x, p.y, cx, cy) > r2) return false;
  return true;
}

// Complete-linkage, radius-bounded agglomeration over a list of groups.
function mergeGroupList(groups: Group[], pinDiameter: number, rCap: number): Group[] {
  const result = groups.slice();
  const pd2 = pinDiameter * pinDiameter;
  for (;;) {
    let bi = -1;
    let bj = -1;
    let bestD = Infinity;
    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const d = distSq(result[i].cx, result[i].cy, result[j].cx, result[j].cy);
        // Closest valid pair wins; ties resolve to the first pair scanned (lowest i, then j),
        // which keeps the partition deterministic for a given input order.
        if (d >= pd2 || d >= bestD) continue; // only overlapping centres, and only if closer than current best
        if (!mergeFits(result[i], result[j], rCap)) continue;
        bi = i;
        bj = j;
        bestD = d;
      }
    }
    if (bi === -1) break;
    const merged = makeGroup(
      result[bi].pts.concat(result[bj].pts),
      result[bi].ids.concat(result[bj].ids),
    );
    result.splice(bj, 1); // remove higher index first
    result.splice(bi, 1, merged);
  }
  return result;
}

// For each point, the index of the nearest centre within rCap, or -1 if none covers it.
export function assignToNearest(
  points: PixelPoint[],
  centres: { x: number; y: number }[],
  rCap: number,
): number[] {
  const r2 = rCap * rCap;
  return points.map(p => {
    let best = -1;
    let bestD = r2;
    for (let i = 0; i < centres.length; i++) {
      const d = distSq(p.x, p.y, centres[i].x, centres[i].y);
      if (d <= bestD) {
        // <= so the rCap boundary is inclusive; ties resolve to the lower index
        if (d < bestD || best === -1) {
          bestD = d;
          best = i;
        }
      }
    }
    return best;
  });
}

function signature(groups: Group[]): string {
  return groups
    .map(g => g.ids.slice().sort().join(','))
    .sort()
    .join('|');
}

// Public formation entry: start from singletons, return the partition as id-lists.
export function formClusters(
  points: PixelPoint[],
  pinDiameter: number,
  rCap: number = pinDiameter,
): string[][] {
  const singletons = points.map(p => makeGroup([{ x: p.x, y: p.y }], [p.id]));
  return mergeGroupList(singletons, pinDiameter, rCap).map(g => g.ids);
}

// Two-phase clustering: complete-linkage formation, then nearest-centre reassignment
// iterated until the partition is stable (or maxIters reached). rCap defaults to pinDiameter.
export function clusterPoints(
  points: PixelPoint[],
  pinDiameter: number,
  maxIters = 5,
): string[][] {
  const rCap = pinDiameter;
  if (points.length === 0) return [];

  let groups = mergeGroupList(
    points.map(p => makeGroup([{ x: p.x, y: p.y }], [p.id])),
    pinDiameter,
    rCap,
  );
  let sig = signature(groups);

  for (let iter = 0; iter < maxIters; iter++) {
    const centres = groups.map(g => ({ x: g.cx, y: g.cy }));
    const assignment = assignToNearest(points, centres, rCap);

    // Rebuild groups from the assignment. mergeGroupList guarantees every member is within
    // rCap of its group's centroid, so each point always has a covering centre (its own
    // group's) and assignToNearest never returns -1 here; the solo branch is defensive.
    const buckets = new Map<number, PixelPoint[]>();
    const solos: PixelPoint[] = [];
    points.forEach((p, k) => {
      const a = assignment[k];
      if (a < 0) {
        solos.push(p);
        return;
      }
      const bucket = buckets.get(a);
      if (bucket) bucket.push(p);
      else buckets.set(a, [p]);
    });

    let rebuilt: Group[] = [];
    for (const pts of buckets.values()) {
      rebuilt.push(makeGroup(pts.map(p => ({ x: p.x, y: p.y })), pts.map(p => p.id)));
    }
    for (const p of solos) {
      rebuilt.push(makeGroup([{ x: p.x, y: p.y }], [p.id]));
    }

    rebuilt = mergeGroupList(rebuilt, pinDiameter, rCap);
    const nsig = signature(rebuilt);
    groups = rebuilt;
    if (nsig === sig) break;
    sig = nsig;
  }

  return groups.map(g => g.ids);
}
